import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../../Translation";
import Badge from "../../Badge";
import { ArrowDownIcon, ArrowsPointingOutIcon, ChevronDownIcon, Cog8ToothIcon, DocumentIcon, ExclamationCircleIcon, MinusIcon, PlusIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { split, webhook } from "../../../Resources/Images";
import { TextInput } from "../../Inputs/TextInput";
import { TextArea } from "../../Inputs/TextArea";
import { Button } from "../../Buttons";
import Tabs, { useTabs } from "../../Tabs";
import { useAPI } from "../../../API";
import Select from "../../Inputs/Select";
import { bgColor, hoverBgColor } from "../../../Resources/CSS/styling";
import Radio from "../../Inputs/Radio";


export const useConditionStep = (step) => {
    const isContactInTag = step.data.operation === 'CONTACT_IS_IN_TAG';
    const isEventCheckField = step.data.variable.toLowerCase().startsWith('event.');
    const isContactCheckField = step.data.variable.toLowerCase().startsWith('contact.');
    const isGlobalCheckField = step.data.variable.toLowerCase().startsWith('global.');
    return { isContactInTag, isEventCheckField, isContactCheckField, isGlobalCheckField };
}

const ConfigurationTab = ({ tmpStep, setTmpStep, save, error, isCreate, tags = [] }) => {

    const { translate } = useTranslation();
    const api = useAPI();

    const { isContactInTag, isEventCheckField, isContactCheckField, isGlobalCheckField } = useConditionStep(tmpStep);


    const setTag = (tag) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value: tag.id } });
    }

    const selectedTag = tags.length > 0 ? tags.find((tag) => tag.id === tmpStep.data.value) || tags[0] : null;

    const variableWithoutSource = (variable) => {
        if (variable.startsWith('event')) {
            return variable.substring(6) || '';
        } else if (variable.startsWith('contact')) {
            return variable.substring(8) || '';
        } else if (variable.startsWith('global')) {
            return variable.substring(7) || '';
        }
        return '';
    }

    const setVariable = (variable) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, variable: `${isContactCheckField ? 'contact' : isGlobalCheckField ? 'global' : 'event'}.${variable}` } });
    }

    const variableTypes = [
        { id: 1, name: translate('automatisation.string'), value: 'string' },
        { id: 2, name: translate('automatisation.number'), value: 'number' },
        { id: 3, name: translate('automatisation.boolean'), value: 'boolean' }
    ]

    const selectedVariableType = variableTypes.find((type) => type.value.toLowerCase() === tmpStep.data.variable_type.toLowerCase()) || variableTypes[0];

    const setVariableType = (type) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, variable_type: type.value, value: type.value == 'boolean' ? true : '' } });
    }

    const booleanValues = [
        { id: 1, name: translate('automatisation.true'), value: true },
        { id: 2, name: translate('automatisation.false'), value: false }
    ];

    const selectedBooleanValue = booleanValues.find((value) => value.value === tmpStep.data.value) || booleanValues[0];

    const setValue = (value) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value: value } });
    }

    const operations = [
        { id: 1, name: translate('automatisation.is_equal'), value: 'EQUALS' },
        { id: 2, name: translate('automatisation.is_not_equal'), value: 'NOT_EQUALS' },
        ...(selectedVariableType.value === 'number' || selectedVariableType.value === 'string' ? [{ id: 3, name: translate('automatisation.is_greater'), value: 'GREATER_THAN' },{ id: 4, name: translate('automatisation.is_less'), value: 'LESS_THAN' }] : []),
        ...(selectedVariableType.value === 'string' ? [{ id: 5, name: translate('automatisation.contains'), value: 'CONTAINS' }, { id: 6, name: translate('automatisation.does_not_contain'), value: 'NOT_CONTAINS' }] : []),
        { id: 7, name: translate('automatisation.is_null_or_empty'), value: 'IS_NULL_OR_EMPTY' },
        { id: 8, name: translate('automatisation.is_not_null_or_empty'), value: 'IS_NOT_NULL_OR_EMPTY' },
    ];


    const selectedOperation = operations.find((operation) => operation.value.toLowerCase() === tmpStep.data.operation.toLowerCase()) || operations[0];

    const selectedOperationRequiredValue = !(selectedOperation.value === 'IS_NULL_OR_EMPTY' || selectedOperation.value === 'IS_NOT_NULL_OR_EMPTY');

    const setOperation = (operation) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, operation: operation.value } });
    }

    const keepFlowOptions = [
        { id: 1, name: translate('automatisation.keep_flow_in_true_flow'), value: true },
        { id: 2, name: translate('automatisation.keep_flow_in_false_flow'), value: false },
        { id: 3, name: translate('automatisation.dont_keep_flow'), value: null },
    ];

    const selectedKeepFlow = keepFlowOptions.find((option) => option.value === tmpStep.data.keep_flow) || keepFlowOptions[2];

    const setKeepFlow = (keepFlowId) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, keep_flow: keepFlowOptions.find((option) => option.id === keepFlowId).value } });
    }

    console.log(tmpStep);

    return (
        <div className="flex flex-col mt-4 overflow-visible">

            {error && <p className="text-red-600 flex flex-row items-center bg-red-50 p-4"><ExclamationCircleIcon className="w-6 h-6 mr-2" />{error}</p>}

            {isContactInTag && tags && <div>
                <h3 className="text-xl font-bold">{translate('automatisation.tag')}</h3>
                <Select selected={selectedTag} options={tags} setSelected={(e) => setTag(e)} />
            </div>}

            {(isContactCheckField || isEventCheckField || isGlobalCheckField) && <div>
                <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.variable')}</p>
                <TextInput value={variableWithoutSource(tmpStep.data.variable)} onChange={(e) => setVariable(e.target.value)} />

                <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.variable_type')}</p>
                <Select selected={selectedVariableType} options={variableTypes} setSelected={(e) => setVariableType(e)} />

                <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.operation')}</p>
                <Select selected={selectedOperation} options={operations} setSelected={(e) => setOperation(e)} />

                {selectedOperationRequiredValue && <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.value')}</p>}
                {(selectedVariableType.value === 'string' || selectedVariableType.value === 'number') && selectedOperationRequiredValue && <TextInput type={selectedVariableType.value === 'number' ? 'number' : 'text'} value={tmpStep.data.value} onChange={(e) => setValue(e.target.value)} />}
                {selectedVariableType.value === 'boolean' && selectedOperationRequiredValue && <Select selected={selectedBooleanValue} options={booleanValues} setSelected={(e) => setValue(e.value)} />}
            </div>}

            {isCreate && <div className="mt-4 flex flex-col">
                <h3 className="text-xl font-bold">{translate('automatisation.condition_keep_flue')}</h3>
                <Radio direction="flex-col" options={keepFlowOptions} selected={selectedKeepFlow} onChange={setKeepFlow} />
            </div>}

            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">

                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep)}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

const DescriptionTab = ({ tmpStep, setTmpStep, save, isCreate }) => {
    const { translate } = useTranslation();
    return (
        <div className="flex flex-col mt-4">
            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.description')}</p>
            <TextArea rows={5} value={tmpStep.description} onChange={(e) => setTmpStep({ ...tmpStep, description: e.target.value })} />

            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep, JSON.stringify(tmpStep.data.body))}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

export default function ConditionPanel({ panelHook, step, refresh, deleteStep, onCreateNewStep, isCreate }) {
    const { translate } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [tmpStep, setTmpStep] = useState({ ...step });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const api = useAPI();

    const [tags, setTags] = useState([]);
    // For autocompletion
    useEffect(() => {
        api.listTags(1,99999).then((result) => setTags(result.data.tags));
    }, []);

    const tabs = useTabs([
        { icon: Cog8ToothIcon, name: translate('automatisation.configuration'), current: true },
        { icon: DocumentIcon, name: translate('automatisation.description'), current: false }
    ]);
    const { isContactInTag, isEventCheckField, isContactCheckField, isGlobalCheckField } = useConditionStep(step);

    // Reset tmpStep when panel is closed
    useEffect(() => {
        if (!panelHook.open) {
            setTmpStep({ ...step });
        }
    }, [panelHook.open]);

    const save = async (tmpStep) => {


        try {
           
            if (isCreate) {

                if (!tmpStep.description || tmpStep.description.trim() === '') {
                    if (isContactInTag) {
                        const tag = tags.find((tag) => tag.id === tmpStep.data.value);
                        tmpStep.description = "Verify if contact is in tag " + (tag ? tag.name : tmpStep.data.value);
                    } else {

                        const variableName = tmpStep.data.variable.split('.').filter((x, index) => index > 0).join('.');

                        if (isEventCheckField) {
                            tmpStep.description = "Verify variable " + variableName + " in event";
                        } else if (isContactCheckField) {
                            tmpStep.description = "Verify variable " + variableName + " in contact";
                        } else if (isGlobalCheckField) {
                            tmpStep.description = "Verify variable " + variableName + " in global";
                        }
                    }
                }

                const newStepResponse = await api.createAutomatisationStep(tmpStep.automatisation_id, tmpStep);
                if (newStepResponse?.data?.automatisation_step) {
                    await onCreateNewStep(newStepResponse.data.automatisation_step, tmpStep.data.keep_flow);
                }
            } else {
                await api.updateAutomatisationStep(tmpStep.automatisation_id, tmpStep);
            }

            panelHook.setOpen(false);
            setTimeout(() => {
                refresh();
            }, 1000);
        } catch (e) {
            setError('Invalid JSON');
            return;
        }

    }


    return (
        <div className="flex flex-col flex-1 rounded-md p-4 overflow-hidden h-full w-96">
            <div className="flex flex-row items-center">
                <img src={split} alt="split" className="w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md mr-2 text-black" />
                <div className="flex flex-col ml-2">
                    <h2 className="text-xl font-bold">{translate('automatisation.condition')}</h2>
                    <h3 className="text-gray-600 font-medium text-wrap break-all">{translate(isContactInTag ? 'automatisation.contact_is_in_tag' : isEventCheckField ? 'automatisation.event_check_field' : isContactCheckField ? 'automatisation.contact_check_field' : isGlobalCheckField ? 'automatisation.global_check_field' : '')}</h3>
                </div>
                
                <div className="flex flex-row flex-1 justify-end">

               
                    {isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => save(tmpStep)}>{translate('create')}</Button>}
                </div>
            </div>

            {!isCreate && 
                <div className="flex flex-col mt-4 gap-2">
                    <Button className="bg-red-600 hover:bg-red-300 ml-4" onClick={() => deleteStep(tmpStep, 'none')}>
                        {translate('delete_none_flow')}
                    </Button>
                    <Button className="bg-red-600 hover:bg-red-300 ml-4" onClick={() => deleteStep(tmpStep, 'false')}>
                        {translate('delete_false_flow')}
                    </Button>
                    <Button className="bg-red-600 hover:bg-red-300 ml-4" onClick={() => deleteStep(tmpStep, 'true')}>
                        {translate('delete_true_flow')}
                    </Button>
                </div>}

            <div className="flex flex-row overflow-x-scroll no-scrollbar">
                <Tabs tabs={tabs} />
            </div>

            {tabs.currentTabIndex === 0 && <ConfigurationTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} tags={tags} />}
            {tabs.currentTabIndex === 1 && <DescriptionTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} />}
        </div>
    )
}